/* Monthly Reason Report — deletes / edits / cancels per person × reason (Order Register audit). */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, RefreshCw } from "lucide-react";

// Backend months are IST.
const thisMonth = () => new Date(Date.now() + 330 * 60000).toISOString().slice(0, 7);

const COLS = [["deletes", "Deletes"], ["edits", "Edits"], ["cancels", "Cancels"], ["total", "Total"]];

const downloadCsv = (data) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [["Person", "Reason", ...COLS.map(c => c[1])].map(esc).join(",")];
  data.rows.forEach(r => lines.push([r.person, r.reason, ...COLS.map(c => r[c[0]])].map(esc).join(",")));
  lines.push(["TOTAL", "", ...COLS.map(c => data.totals[c[0]])].map(esc).join(","));
  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url; a.download = `reason-report-${data.month}.csv`; a.click();
  URL.revokeObjectURL(url);
};

export const ReasonReport = () => {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/order-register/reason-report`, { params: { month }, headers: getAuthHeader() });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load reason report");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 space-y-3" data-testid="reason-report">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44" data-testid="reason-report-month" />
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="reason-report-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <Button size="sm" onClick={() => downloadCsv(data)} disabled={!data?.rows?.length} data-testid="reason-report-csv">
          <Download className="w-4 h-4 mr-1" /> CSV
        </Button>
      </div>
      {data && !data.rows.length && <div className="text-sm text-slate-500 py-8 text-center" data-testid="reason-report-empty">No deletes, edits or cancels with reasons in {data.month}.</div>}
      {data?.rows?.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-slate-200" data-testid="reason-report-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2">Person</th>
                <th className="text-left px-3 py-2">Reason</th>
                {COLS.map(([k, l]) => <th key={k} className="text-right px-3 py-2">{l}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`reason-report-row-${i}`}>
                  <td className="px-3 py-1.5 font-medium">{r.person}</td>
                  <td className="px-3 py-1.5">{r.reason}</td>
                  {COLS.map(([k]) => <td key={k} className={`px-3 py-1.5 text-right tabular-nums ${k === "total" ? "font-semibold" : ""}`}>{r[k] || ""}</td>)}
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-100 font-semibold" data-testid="reason-report-totals">
              <tr>
                <td className="px-3 py-2" colSpan={2}>Total</td>
                {COLS.map(([k]) => <td key={k} className="px-3 py-2 text-right tabular-nums">{data.totals[k]}</td>)}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
